import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Scale, Trash2, TrendingUp, TrendingDown, Minus, Calendar, Percent, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ScaleScreenshotParser from './ScaleScreenshotParser';
import FFMICalculator from './FFMICalculator';

interface WeightTrackerProps {
  selectedDate: string;
  onWeightChange: () => void;
  refreshTrigger: number;
  clientId?: number;
}

// Custom tooltip for the trend chart
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-sm p-2.5 shadow-md text-xs font-mono space-y-1">
      <div className="text-muted-foreground font-bold border-b border-border/50 pb-1 mb-1">{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-bold text-foreground">{p.value != null ? p.value : '—'}</span>
        </div>
      ))}
    </div>
  );
};

export default function WeightTracker({ selectedDate, onWeightChange, refreshTrigger, clientId }: WeightTrackerProps) {
  const isClientView = !!clientId;
  const [weightInput, setWeightInput] = useState('');
  const [bodyFatInput, setBodyFatInput] = useState('');
  const [notesInput, setWeightNotesInput] = useState('');
  const [profileRefresh, setProfileRefresh] = useState(0);

  const utils = trpc.useUtils();

  const { data: ownLogs = [] } = trpc.weight.list.useQuery(undefined, { enabled: !isClientView });
  const { data: clientLogs = [] } = trpc.trainerPortal.getClientWeightLogs.useQuery(
    { clientId: clientId! },
    { enabled: isClientView && !!clientId }
  );
  const rawLogs = isClientView ? clientLogs : ownLogs;

  const logs = rawLogs.map((l) => ({
    id: l.id,
    date: l.date,
    weight: l.weight,
    bodyFat: l.bodyFat ?? undefined,
    notes: l.notes ?? undefined,
  }));

  useEffect(() => {
    const todayLog = logs.find((l) => l.date === selectedDate);
    if (todayLog) {
      setWeightInput(todayLog.weight.toString());
      setBodyFatInput(todayLog.bodyFat ? todayLog.bodyFat.toString() : '');
      setWeightNotesInput(todayLog.notes || '');
    } else {
      setWeightInput('');
      setBodyFatInput('');
      setWeightNotesInput('');
    }
  }, [selectedDate, rawLogs.length]);

  const saveWeightMutation = trpc.weight.save.useMutation({
    onSuccess: () => {
      utils.weight.list.invalidate();
      onWeightChange();
    },
  });

  const deleteWeightMutation = trpc.weight.delete.useMutation({
    onSuccess: () => {
      utils.weight.list.invalidate();
      onWeightChange();
    },
  });

  const handleSaveWeight = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedWeight = parseFloat(weightInput);
    if (isNaN(parsedWeight) || parsedWeight <= 0) {
      toast.error('Please enter a valid positive weight value');
      return;
    }
    const parsedBodyFat = parseFloat(bodyFatInput);
    const bodyFat = isNaN(parsedBodyFat) || parsedBodyFat <= 0 ? undefined : parsedBodyFat;
    await saveWeightMutation.mutateAsync({
      date: selectedDate,
      weight: parsedWeight,
      bodyFat,
      notes: notesInput.trim() || undefined,
    });
    toast.success(`Logged ${parsedWeight} lbs${bodyFat ? ` & ${bodyFat}% BF` : ''} for ${selectedDate}`);
  };

  const handleDeleteLog = async (id: string, date: string) => {
    if (confirm(`Delete weight log for ${date}?`)) {
      await deleteWeightMutation.mutateAsync({ logId: id });
      toast.success('Weight log deleted');
    }
  };

  const sortedLogs = [...logs].sort((a, b) => a.date.localeCompare(b.date));
  const chartData = sortedLogs.slice(-20).map((l) => ({
    date: format(new Date(l.date + 'T00:00:00'), 'MMM d'),
    Weight: l.weight,
    'Body Fat %': l.bodyFat ?? null,
  }));

  const latestLog = sortedLogs.length > 0 ? sortedLogs[sortedLogs.length - 1] : null;
  const latestWeight = latestLog?.weight ?? null;
  const latestBodyFat = latestLog
    ? ([...sortedLogs].reverse().find((l) => l.bodyFat !== undefined)?.bodyFat ?? null)
    : null;
  const earliestWeight = sortedLogs.length > 0 ? sortedLogs[0].weight : null;
  const netChange = latestWeight !== null && earliestWeight !== null
    ? parseFloat((latestWeight - earliestWeight).toFixed(1))
    : null;

  // Trend: compare last 3 vs previous 3
  const getTrend = () => {
    if (sortedLogs.length < 4) return 'neutral';
    const recent = sortedLogs.slice(-3).reduce((s, l) => s + l.weight, 0) / 3;
    const prior = sortedLogs.slice(-6, -3).reduce((s, l) => s + l.weight, 0) / Math.min(3, sortedLogs.slice(-6, -3).length);
    if (recent > prior + 0.5) return 'up';
    if (recent < prior - 0.5) return 'down';
    return 'neutral';
  };
  const trend = getTrend();

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {/* ── Left Column: Logger + FFMI ── */}
      <div className="md:col-span-4 space-y-5">
        {/* Scale Logger */}
        <div className="forge-panel p-4 space-y-4">
          <div className="forge-header">
            <span>Form</span>
            <span className="text-primary font-mono flex items-center gap-1">
              <Scale className="h-3.5 w-3.5" /> SCALE
            </span>
          </div>

          {!isClientView && (
            <>
              <ScaleScreenshotParser
                onParsed={async (weight, bodyFat, notes) => {
                  setWeightInput(weight.toString());
                  setBodyFatInput(bodyFat ? bodyFat.toString() : '');
                  setWeightNotesInput(notes);
                  await saveWeightMutation.mutateAsync({ date: selectedDate, weight, bodyFat, notes });
                }}
              />
              <div className="text-[10px] font-mono text-muted-foreground text-center tracking-widest">— or log manually —</div>
              <form onSubmit={handleSaveWeight} className="space-y-3">
                <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {selectedDate}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Weight (LBS)</label>
                    <div className="relative">
                      <Input type="number" step="0.1" placeholder="185.4" value={weightInput} onChange={(e) => setWeightInput(e.target.value)} className="bg-secondary border-border font-mono text-base pr-10" />
                      <span className="absolute right-2 top-2.5 text-[10px] font-mono text-muted-foreground">LBS</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Body Fat (%)</label>
                    <div className="relative">
                      <Input type="number" step="0.1" placeholder="12.5" value={bodyFatInput} onChange={(e) => setBodyFatInput(e.target.value)} className="bg-secondary border-border font-mono text-base pr-8" />
                      <span className="absolute right-2 top-2.5 text-[10px] font-mono text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-mono text-muted-foreground uppercase mb-1">Notes</label>
                  <Input placeholder="Fasted, post-workout…" value={notesInput} onChange={(e) => setWeightNotesInput(e.target.value)} className="bg-secondary border-border text-xs" />
                </div>
                <Button type="submit" disabled={saveWeightMutation.isPending} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest">
                  RECORD WEIGHT
                </Button>
              </form>
            </>
          )}

          {/* Stats strip */}
          {latestWeight !== null ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary/40 border border-border/60 rounded-sm p-3 space-y-0.5">
                <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Current</div>
                <div className="text-2xl font-mono font-black text-primary leading-none">{latestWeight}</div>
                <div className="text-[10px] font-mono text-muted-foreground">LBS{latestBodyFat ? ` · ${latestBodyFat}% BF` : ''}</div>
              </div>
              <div className="bg-secondary/40 border border-border/60 rounded-sm p-3 space-y-0.5">
                <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Net Progress</div>
                {netChange !== null ? (
                  <>
                    <div className={`text-2xl font-mono font-black leading-none flex items-center gap-1 ${netChange > 0 ? 'text-destructive' : netChange < 0 ? 'text-success' : 'text-muted-foreground'}`}>
                      {netChange > 0 ? <ArrowUpRight className="h-4 w-4" /> : netChange < 0 ? <ArrowDownRight className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                      {netChange > 0 ? `+${netChange}` : netChange}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground">LBS all-time</div>
                  </>
                ) : (
                  <div className="text-xl font-mono font-black text-muted-foreground/30">—</div>
                )}
              </div>
            </div>
          ) : (
            !isClientView && (
              <div className="border border-dashed border-border/60 rounded-sm p-4 text-center space-y-1">
                <Scale className="h-5 w-5 text-muted-foreground/40 mx-auto" />
                <div className="text-[11px] font-mono text-muted-foreground">No weight data yet.</div>
                <div className="text-[10px] text-muted-foreground/70">Log your first weigh-in above to start tracking.</div>
              </div>
            )
          )}
        </div>

        {/* FFMI Hero */}
        <FFMICalculator latestWeight={latestWeight} latestBodyFat={latestBodyFat} onProfileChange={() => setProfileRefresh((prev) => prev + 1)} />
      </div>

      {/* ── Right Column: Trend Graph + History ── */}
      <div className="md:col-span-8 space-y-5">
        {/* Trend Graph */}
        <div className="forge-panel p-4 space-y-4">
          <div className="forge-header">
            <span>Trends</span>
            <span className={`font-mono flex items-center gap-1 text-[10px] ${trend === 'up' ? 'text-destructive' : trend === 'down' ? 'text-success' : 'text-muted-foreground'}`}>
              {trend === 'up' ? <TrendingUp className="h-3.5 w-3.5" /> : trend === 'down' ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
              {trend === 'up' ? 'TRENDING UP' : trend === 'down' ? 'TRENDING DOWN' : 'STABLE'}
            </span>
          </div>

          {chartData.length < 2 ? (
            <div className="h-[220px] flex flex-col items-center justify-center border border-dashed border-border/60 rounded-sm space-y-2">
              <TrendingUp className="h-8 w-8 text-muted-foreground/25" />
              <div className="text-xs font-mono text-muted-foreground text-center">
                Log weight on <strong className="text-foreground">2+ days</strong> to plot your trend line.
              </div>
            </div>
          ) : (
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.82 0.02 165 / 0.5)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="oklch(0.45 0.04 165)"
                    fontSize={9}
                    fontFamily="var(--font-mono)"
                    tick={{ fill: 'oklch(0.45 0.04 165)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="oklch(0.35 0.12 165)"
                    fontSize={9}
                    fontFamily="var(--font-mono)"
                    tick={{ fill: 'oklch(0.45 0.04 165)' }}
                    axisLine={false}
                    tickLine={false}
                    domain={['dataMin - 3', 'dataMax + 3']}
                    tickFormatter={(v) => `${v}`}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="oklch(0.50 0.23 28)"
                    fontSize={9}
                    fontFamily="var(--font-mono)"
                    tick={{ fill: 'oklch(0.45 0.04 165)' }}
                    axisLine={false}
                    tickLine={false}
                    domain={['dataMin - 1', 'dataMax + 1']}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    yAxisId="left"
                    name="Weight (lbs)"
                    type="monotone"
                    dataKey="Weight"
                    stroke="oklch(0.35 0.12 165)"
                    strokeWidth={2.5}
                    dot={{ fill: 'oklch(0.35 0.12 165)', r: 3, strokeWidth: 0 }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                  <Line
                    yAxisId="right"
                    name="Body Fat %"
                    type="monotone"
                    dataKey="Body Fat %"
                    stroke="oklch(0.50 0.23 28)"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    connectNulls
                    dot={{ fill: 'oklch(0.50 0.23 28)', r: 2.5, strokeWidth: 0 }}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Chart legend */}
          {chartData.length >= 2 && (
            <div className="flex items-center gap-4 pt-1 border-t border-border/30">
              <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                <span className="w-4 h-0.5 bg-primary inline-block rounded" />
                Weight (LBS)
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                <span className="w-4 h-0 border-t-2 border-dashed border-destructive inline-block" />
                Body Fat (%)
              </div>
              <div className="ml-auto text-[10px] font-mono text-muted-foreground">
                Last {Math.min(20, logs.length)} entries
              </div>
            </div>
          )}
        </div>

        {/* Weight Log History */}
        <div className="forge-panel p-4 space-y-3">
          <div className="forge-header">
            <span>Log History</span>
            <span className="text-muted-foreground font-mono text-[10px]">{logs.length} ENTRIES</span>
          </div>

          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-2 border border-dashed border-border/60 rounded-sm">
              <Scale className="h-7 w-7 text-muted-foreground/25" />
              <div className="text-xs font-mono text-muted-foreground">No weight logs yet.</div>
              <div className="text-[10px] text-muted-foreground/60">Your weigh-in history will appear here.</div>
            </div>
          ) : (
            <div className="max-h-[200px] overflow-y-auto space-y-1.5 pr-1">
              {[...logs]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((l, i) => {
                  const prev = [...logs].sort((a, b) => a.date.localeCompare(b.date)).find((x) => x.date < l.date);
                  const delta = prev ? parseFloat((l.weight - prev.weight).toFixed(1)) : null;
                  return (
                    <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-secondary/30 border border-border/40 rounded-sm text-xs group">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-muted-foreground shrink-0">{l.date}</span>
                        <span className="font-mono font-bold text-primary">{l.weight} LBS</span>
                        {l.bodyFat && (
                          <span className="font-mono text-muted-foreground flex items-center gap-0.5 shrink-0">
                            <Percent className="h-3 w-3" />{l.bodyFat}%
                          </span>
                        )}
                        {delta !== null && (
                          <span className={`text-[10px] font-mono shrink-0 ${delta > 0 ? 'text-destructive' : delta < 0 ? 'text-success' : 'text-muted-foreground'}`}>
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        )}
                        {l.notes && <span className="text-muted-foreground truncate text-[10px]">({l.notes})</span>}
                      </div>
                      {!isClientView && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteLog(l.id, l.date)}
                          className="h-6 w-6 text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity shrink-0"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
